import React, { useEffect, useState, useCallback } from "react";
import Layout from "../components/Layout";
import { endpoints } from "../lib/api";
import { Loader2, Trash2, Plus, LogOut, BarChart3, Save, Link as LinkIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import RegistrarLogo from "../components/RegistrarLogo";

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("admin@dompryce.com");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      const res = await endpoints.adminLogin({ email, password });
      localStorage.setItem("dompryce_admin_token", res.data.token);
      onLogin(res.data.admin);
    } catch (e) {
      setErr("Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-md rounded-2xl border border-zinc-200 bg-white p-8 soft-shadow" data-testid="admin-login-card">
      <h1 className="font-display text-2xl font-bold text-zinc-900">Admin login</h1>
      <p className="mt-1 text-sm text-zinc-500">Manage premium domains, ads, and analytics.</p>
      <form onSubmit={submit} className="mt-6 space-y-4">
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-email-input"
            className="mt-2 w-full rounded-xl border border-zinc-200 bg-zinc-50/40 p-3 text-sm outline-none focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
          />
        </div>
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            className="mt-2 w-full rounded-xl border border-zinc-200 bg-zinc-50/40 p-3 text-sm outline-none focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100"
          />
        </div>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-btn"
          className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-6 py-3 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
        </button>
      </form>
    </div>
  );
}

export default function Admin() {
  const [admin, setAdmin] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [ads, setAds] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [tab, setTab] = useState("analytics");
  const [newAd, setNewAd] = useState({ slot: "left", position: 5, title: "", subtitle: "", cta: "Learn more", href: "#", kind: "sponsor", active: true });
  const [newPremium, setNewPremium] = useState({ name: "", category: "AI Startups", estimated_value: 5000, tagline: "" });

  const fetchAll = useCallback(async () => {
    try {
      const [a, ad, af] = await Promise.all([
        endpoints.adminAnalytics(),
        endpoints.adminAds(),
        endpoints.adminAffiliates(),
      ]);
      setAnalytics(a.data);
      setAds(ad.data.items || []);
      setAffiliates(af.data.items || []);
    } catch (e) {
      // token expired
      localStorage.removeItem("dompryce_admin_token");
      setAdmin(null);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("dompryce_admin_token");
    if (token) {
      setAdmin({ email: "admin@dompryce.com" });
      fetchAll();
    }
  }, [fetchAll]);

  const onLogin = (a) => {
    setAdmin(a);
    fetchAll();
  };

  const logout = () => {
    localStorage.removeItem("dompryce_admin_token");
    setAdmin(null);
    setAnalytics(null);
    setAds([]);
  };

  const createAd = async (e) => {
    e.preventDefault();
    try {
      const res = await endpoints.adminAdCreate(newAd);
      setAds((a) => [...a, res.data]);
      toast.success("Ad created");
      setNewAd({ ...newAd, title: "", subtitle: "" });
    } catch (e) { toast.error("Failed"); }
  };

  const toggleAd = async (ad) => {
    const updated = { ...ad, active: !ad.active };
    delete updated.id;
    delete updated.created_at;
    await endpoints.adminAdUpdate(ad.id, updated);
    setAds((arr) => arr.map((x) => (x.id === ad.id ? { ...x, active: !x.active } : x)));
  };

  const deleteAd = async (ad) => {
    await endpoints.adminAdDelete(ad.id);
    setAds((arr) => arr.filter((x) => x.id !== ad.id));
  };

  const updateAffiliate = async (slug, patch) => {
    const current = affiliates.find((a) => a.registrar_slug === slug);
    if (!current) return;
    const next = { ...current, ...patch };
    setAffiliates((arr) => arr.map((a) => (a.registrar_slug === slug ? next : a)));
    try {
      await endpoints.adminAffiliateUpdate(slug, {
        affiliate_id: next.affiliate_id || "",
        enabled: !!next.enabled,
      });
      toast.success(`${current.registrar_name} affiliate updated`);
    } catch (e) {
      toast.error("Update failed");
      // revert on failure
      setAffiliates((arr) => arr.map((a) => (a.registrar_slug === slug ? current : a)));
    }
  };

  const createPremium = async (e) => {
    e.preventDefault();
    try {
      await endpoints.adminPremiumCreate(newPremium);
      toast.success("Premium domain added");
      setNewPremium({ ...newPremium, name: "", tagline: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!admin) {
    return (
      <Layout hideSidebars>
        <div className="py-12">
          <AdminLogin onLogin={onLogin} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout hideSidebars>
      <section data-testid="admin-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">Admin</div>
            <h1 className="mt-2 font-display text-3xl font-bold text-zinc-900 md:text-4xl">Control room</h1>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-medium text-zinc-700 hover:bg-zinc-50" data-testid="admin-logout-btn">
            <LogOut className="h-3 w-3" /> Logout
          </button>
        </div>

        <div className="mt-8 flex gap-2 border-b border-zinc-200">
          {["analytics", "ads", "premium", "affiliates"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium capitalize transition-colors ${
                tab === t ? "border-indigo-600 text-indigo-700" : "border-transparent text-zinc-600 hover:text-zinc-900"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "analytics" && analytics && (
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6" data-testid="admin-analytics">
            {[
              { label: "Users", value: analytics.users },
              { label: "Saved domains", value: analytics.saved_domains },
              { label: "Searches (30d)", value: analytics.searches_30d },
              { label: "AI runs (30d)", value: analytics.ai_runs_30d },
              { label: "Premium listings", value: analytics.premium_listings },
              { label: "Active ads", value: analytics.ads },
            ].map((s) => (
              <div key={s.label} className="rounded-2xl border border-zinc-100 bg-white p-5 soft-shadow">
                <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">{s.label}</div>
                <div className="mt-2 font-display text-3xl font-bold text-zinc-900">{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "ads" && (
          <div className="mt-8" data-testid="admin-ads">
            <form onSubmit={createAd} className="grid grid-cols-1 gap-3 rounded-2xl border border-zinc-200 bg-white p-5 md:grid-cols-3">
              <select value={newAd.slot} onChange={(e) => setNewAd({ ...newAd, slot: e.target.value })} className="rounded-lg border border-zinc-200 px-3 py-2 text-sm">
                <option value="left">Left</option>
                <option value="right">Right</option>
              </select>
              <input type="number" value={newAd.position} onChange={(e) => setNewAd({ ...newAd, position: parseInt(e.target.value) || 1 })} placeholder="Position" className="rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
              <input value={newAd.kind} onChange={(e) => setNewAd({ ...newAd, kind: e.target.value })} placeholder="kind (sponsor/deal/...)" className="rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
              <input value={newAd.title} onChange={(e) => setNewAd({ ...newAd, title: e.target.value })} placeholder="Title" required className="md:col-span-2 rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
              <input value={newAd.cta} onChange={(e) => setNewAd({ ...newAd, cta: e.target.value })} placeholder="CTA" className="rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
              <input value={newAd.subtitle} onChange={(e) => setNewAd({ ...newAd, subtitle: e.target.value })} placeholder="Subtitle" className="md:col-span-2 rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
              <input value={newAd.href} onChange={(e) => setNewAd({ ...newAd, href: e.target.value })} placeholder="Link" className="rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
              <button type="submit" className="md:col-span-3 inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500">
                <Plus className="h-3.5 w-3.5" /> Add ad
              </button>
            </form>

            <div className="mt-6 space-y-2">
              {ads.map((ad) => (
                <div key={ad.id} className="flex items-center gap-4 rounded-xl border border-zinc-100 bg-white p-4">
                  <span className="rounded-md bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-600">{ad.slot} #{ad.position}</span>
                  <div className="flex-1">
                    <div className="font-medium text-zinc-900">{ad.title}</div>
                    <div className="text-xs text-zinc-500">{ad.subtitle}</div>
                  </div>
                  <button onClick={() => toggleAd(ad)} className={`rounded-md px-2 py-1 text-[10px] font-medium uppercase tracking-wider ${ad.active ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{ad.active ? "Active" : "Off"}</button>
                  <button onClick={() => deleteAd(ad)} className="rounded-md p-1.5 text-zinc-400 hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "premium" && (
          <div className="mt-8" data-testid="admin-premium">
            <form onSubmit={createPremium} className="grid grid-cols-1 gap-3 rounded-2xl border border-zinc-200 bg-white p-5 md:grid-cols-2">
              <input value={newPremium.name} onChange={(e) => setNewPremium({ ...newPremium, name: e.target.value })} placeholder="domain.com" required className="rounded-lg border border-zinc-200 px-3 py-2 text-sm font-mono" />
              <input value={newPremium.category} onChange={(e) => setNewPremium({ ...newPremium, category: e.target.value })} placeholder="Category" className="rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
              <input type="number" value={newPremium.estimated_value} onChange={(e) => setNewPremium({ ...newPremium, estimated_value: parseInt(e.target.value) || 0 })} placeholder="Estimated value (USD)" className="rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
              <input value={newPremium.tagline} onChange={(e) => setNewPremium({ ...newPremium, tagline: e.target.value })} placeholder="Tagline" className="rounded-lg border border-zinc-200 px-3 py-2 text-sm" />
              <button type="submit" className="md:col-span-2 inline-flex items-center justify-center gap-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500">
                <Plus className="h-3.5 w-3.5" /> Add premium domain
              </button>
            </form>
            <p className="mt-4 text-sm text-zinc-500"><BarChart3 className="inline h-3 w-3" /> Listings appear in the Premium Domains page immediately.</p>
          </div>
        )}

        {tab === "affiliates" && (
          <div className="mt-8" data-testid="admin-affiliates">
            <div className="rounded-2xl border border-indigo-100 bg-indigo-50/40 p-5 mb-6">
              <div className="flex items-start gap-3">
                <LinkIcon className="h-4 w-4 text-indigo-700 mt-0.5" />
                <div>
                  <div className="font-display text-sm font-semibold text-indigo-900">Affiliate-tag injection</div>
                  <p className="mt-1 text-xs leading-relaxed text-indigo-800/80">
                    When enabled, every "Buy here" link for that registrar is rewritten to include your affiliate ID. Cloudflare doesn't run an affiliate program, so it can't be enabled. Each registrar's affiliate template is hard-coded server-side; you only need to paste your affiliate ID below.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              {affiliates.map((af) => (
                <div
                  key={af.registrar_slug}
                  data-testid={`affiliate-row-${af.registrar_slug}`}
                  className="flex flex-col gap-3 rounded-2xl border border-zinc-200 bg-white p-5 md:flex-row md:items-center md:gap-5"
                >
                  <div className="flex items-center gap-3 md:w-44">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-100 bg-white">
                      <RegistrarLogo registrar={{ name: af.registrar_name, logo_domain: af.logo_domain, icon: "Globe" }} size={24} />
                    </div>
                    <div>
                      <div className="font-display text-sm font-semibold text-zinc-900">{af.registrar_name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-400">
                        {af.supported ? "Affiliate-ready" : "No program"}
                      </div>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={af.affiliate_id || ""}
                    onChange={(e) => setAffiliates((arr) => arr.map((a) => (a.registrar_slug === af.registrar_slug ? { ...a, affiliate_id: e.target.value } : a)))}
                    placeholder={af.supported ? "Paste your affiliate ID" : "Not supported"}
                    disabled={!af.supported}
                    data-testid={`affiliate-input-${af.registrar_slug}`}
                    className="flex-1 rounded-lg border border-zinc-200 bg-zinc-50/40 px-3 py-2 text-sm outline-none focus:border-indigo-300 focus:bg-white focus:ring-2 focus:ring-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => updateAffiliate(af.registrar_slug, { enabled: !af.enabled })}
                      disabled={!af.supported || !(af.affiliate_id || "").trim()}
                      data-testid={`affiliate-toggle-${af.registrar_slug}`}
                      className={`rounded-md px-2 py-1 text-[10px] font-medium uppercase tracking-wider transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                        af.enabled ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"
                      }`}
                    >
                      {af.enabled ? "Enabled" : "Off"}
                    </button>
                    <button
                      onClick={() => updateAffiliate(af.registrar_slug, {})}
                      disabled={!af.supported}
                      data-testid={`affiliate-save-${af.registrar_slug}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-zinc-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Save className="h-3 w-3" /> Save
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-6 text-xs text-zinc-500">
              <ExternalLink className="inline h-3 w-3" /> Sign up: Namecheap on Impact, GoDaddy on CJ Affiliate, Porkbun via porkbun.com/affiliate, Spaceship via ShareASale, Hostinger via hostinger.com/affiliates, Dynadot via dynadot.com/affiliate.
            </p>
          </div>
        )}
      </section>
    </Layout>
  );
}
